import './ProductList.scss';

import { getProductListHtmlByAjax } from '@apis/ajax';
import type { RegistryComponentProps } from '@boost-sd/components-registry';
import { registryComponent } from '@boost-sd/components-registry/registry';
import { DeferredList } from '@boost-sd/core-js';
import { getQueryParamByKey, setQueryParam } from '@boost-sd/core-js/history';
import { ProductItemListViewLayout } from '@components/ProductItem';
import ProductListPlaceholder from '@components/ProductListPlaceholder';
import {
  CURRENT_PER_TOTAL,
  DEFAULT,
  INFINITE,
  LOAD_MORE,
  PREV_PAGE,
  SELECTED_PRODUCT_ID,
  SELECTED_PRODUCT_PAGE,
} from '@constants/pagination';
import useGeneralSettings from '@hooks/useGeneralSettings';
import { useAdditionalElementState } from '@providers/AdditionalElementProvider';
import { useFilterState } from '@providers/FilterProvider';
import type { ProductBanner, ProductListItem } from '@providers/ProductListProvider';
import { useProductListState } from '@providers/ProductListProvider';
import { useAdditionalElementThemeSettings } from '@providers/ThemeProvider/Provider/AdditionalElementThemeSettings';
import { useProductItemThemeSettings } from '@providers/ThemeProvider/Provider/ProductItemThemeSettings';
import { createClsNameMap, getSessionStorage, mergeModifiers, setSessionStorage } from '@utils';
import classnames from '@utils/classnames';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import ProductItem from '../ProductItem';
import { useConnectViewAsOption } from './connectors/connectViewAsOptionState';
import type { ProductListPaginationProps } from './Pagination';
import ProductListPagination from './Pagination';
import useKeepPositionOnBack from './useKeepPositionOnBack';

export const clsNameMap = createClsNameMap({
  modifiers: ['top', 'center', 'bottom'],
  elements: {
    link: createClsNameMap(),
    image: createClsNameMap(),
  },
})('product-banner');

export type ProductListProps = RegistryComponentProps<
  {
    className?: string;
    pagination?: {
      enable: boolean;
    };
    scrollToTopContainerOnLoadProducts?: {
      enable: boolean;
      behavior?: ScrollBehavior;
      target?: 'product-list' | 'window' | HTMLElement;
    };
    callbackFuncAppIntegration?: (() => void)[];
    enableKeepScrollbackPosition?: boolean;
    scrollBackOffset?: number;
    scrollBackAwait?: number;
    afterRender?: () => void;
    disableDeferredRender?: boolean;
  },
  {
    getProductHandles?: () => string[];
  }
>;

const ProductList = ({
  className,
  pagination = {
    enable: true,
  },
  scrollToTopContainerOnLoadProducts = { enable: true, behavior: 'smooth' },
  callbackFuncAppIntegration,
  enableKeepScrollbackPosition = true,
  scrollBackOffset,
  scrollBackAwait,
  helpersRef,
  afterRender,
  disableDeferredRender = true,
}: ProductListProps) => {
  const productListElementRef = useRef<HTMLDivElement | null>(null);
  const { props: viewAsOptionProps } = useConnectViewAsOption();

  const {
    products,
    totalProducts,
    pagination: paginationState,
    changeProductPage,
    currentCollectionSelected,
    banners,
  } = useProductListState();

  const { loadingAdditional, viewAsGridMultiColListOption } = useAdditionalElementState();

  const [limitDisplayProducts, setLimitDisplayProducts] = useState({
    // enable by default if device's width is mobile and not want to keep position on back
    // TODO: review solution later
    enabled: false,
    limit: 4,
  });

  useImperativeHandle(helpersRef, () => ({
    getProductHandles() {
      const getCurrentProductIds = products
        .slice(-(paginationState?.limit || 24))
        .map((item) => item.handle);

      return getCurrentProductIds;
    },
    getProductListHtmlByAjax,
    setAjaxLoading,
  }));

  const {
    sharedAdditionalElementData: { limit },
    setAjaxLoading,
  } = useFilterState();

  const {
    pagination: { paginationType },
  } = useAdditionalElementThemeSettings();

  const {
    general: { borderLayout },
  } = useProductItemThemeSettings();

  // scroll restoration
  const { performScrollRestoration } = useKeepPositionOnBack(products, {
    enableKeepScrollbackPosition,
    scrollBackOffset,
    scrollBackAwait,
  });

  const {
    generalSettings: { no_image_url, addCollectionToProductUrl: hasCollection },
  } = useGeneralSettings();

  const trackPosition = (index: number, productId: number) => {
    const pageParam = getQueryParamByKey('page');
    const currPage = pageParam ? Number(pageParam) : 1;
    const page = Math.ceil((index + 1) / limit) || 1;
    const prevPage = getSessionStorage(PREV_PAGE);
    const calc = {
      [DEFAULT]: currPage,
      [LOAD_MORE]: prevPage - 1 + page,
      [INFINITE]: prevPage - 1 + page,
      [CURRENT_PER_TOTAL]: currPage,
    };

    if (!paginationType) return;

    const actualPage = calc[paginationType];

    setSessionStorage(SELECTED_PRODUCT_PAGE, calc[paginationType]);
    setSessionStorage(SELECTED_PRODUCT_ID, productId);

    setQueryParam('page', actualPage.toString(), true);
  };

  const productListPaginationProps: ProductListPaginationProps = {
    products,
    totalProducts,
    productListElementRef,
    scrollToTopContainerOnLoadProducts,
    paginationState,
    changeProductPage,
    enable: pagination.enable,
    currentCollectionSelected,
  };

  const getColClassName = () => {
    return `product-list-${viewAsOptionProps.viewAsOption}-col`;
  };

  const autoAspectRatio = useMemo(() => {
    return products.reduce((maxRatio, currentProduct) => {
      const firstImage = currentProduct.images_info.length > 0 && currentProduct.images_info[0];
      if (!firstImage) return maxRatio;

      const { height, width } = firstImage;

      return Number(Math.max(height / width, maxRatio).toFixed(2));
    }, 0);
  }, [products]);

  useEffect(() => {
    if (callbackFuncAppIntegration) {
      callbackFuncAppIntegration.forEach((callback: () => void) => callback?.());
    }
  }, [viewAsOptionProps.viewAsOption]);

  const displayProducts = useMemo(() => {
    if (limitDisplayProducts.enabled) return products.slice(0, limitDisplayProducts.limit);

    return products;
  }, [products, limitDisplayProducts]);

  const afterDeferredListRender = () => {
    if (afterRender) {
      afterRender();
      performScrollRestoration();

      const productListElement = productListElementRef.current;

      if (limitDisplayProducts.enabled && productListElement) {
        const intersection = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setLimitDisplayProducts((prev) => ({
                  ...prev,
                  enabled: false,
                }));

                intersection.disconnect();
              }
            });
          },
          {
            rootMargin: '0px 0px 10px 0px',
            threshold: 1,
          }
        );

        intersection.observe(productListElement);
      }
    }
  };

  const renderInlineBanner = (
    banner: ProductBanner,
    cropPosition?: 'top' | 'center' | 'bottom'
  ) => {
    if (banner) {
      return (
        <div className='boost-sd__product-item boost-sd__product-item-grid-view-layout'>
          <div className={mergeModifiers(clsNameMap, cropPosition ? [[cropPosition]] : [])}>
            <a
              className={clsNameMap.elm('link')}
              href={banner.link}
              target={banner.openInNewTab ? '_blank' : '_self'}
              rel='noreferrer'
            >
              <img
                className={clsNameMap.elm('image')}
                width='auto'
                height='auto'
                src={banner.imgSrc}
                alt={banner.name}
              />
            </a>
          </div>
        </div>
      );
    }

    return null;
  };

  const mergeDisplayProductsWithBanners = (
    products: ProductListItem[],
    banners: ProductBanner[],
    currentPage: number,
    limit: number,
    totalProducts: number // <-- pass the actual total number of products
  ) => {
    const sortedBanners = [...banners].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    // Calculate the actual total number of pages (including inserted banners)
    const totalItems = totalProducts;
    let bannerCount = 0;
    let i = 0;
    while (i < sortedBanners.length) {
      // Banner insertion position is calculated based on the total number of items already present
      if ((sortedBanners[i].position ?? 0) <= totalItems + bannerCount) {
        bannerCount++;
      }
      i++;
    }
    const totalPages = Math.ceil((totalProducts + bannerCount) / limit);
    const isLastPage = currentPage === totalPages;

    // Recalculate bannerCount for the current page
    bannerCount = 0;
    if (currentPage > 1) {
      const maxIndexBeforePage = limit * (currentPage - 1);
      for (const banner of sortedBanners) {
        const virtualIndex = (banner.position ?? 0) - bannerCount;
        if (virtualIndex < maxIndexBeforePage) {
          bannerCount += 1;
        } else {
          break;
        }
      }
    }
    let bannerIdx = bannerCount;
    const currentPos = limit * (currentPage - 1);
    let index = currentPos + bannerCount;
    const result: (
      | { type: 'product'; data: ProductListItem }
      | { type: 'banner'; data: ProductBanner }
    )[] = [];

    let productIdx = 0;
    let productsOnPage = 0;

    while (productIdx < products.length && productsOnPage < limit) {
      // Insert banner at the correct position
      while (
        bannerIdx < sortedBanners.length &&
        (sortedBanners[bannerIdx].position ?? 0) === index
      ) {
        result.push({ type: 'banner', data: sortedBanners[bannerIdx] });
        bannerIdx += 1;
        index += 1;
      }
      result.push({ type: 'product', data: products[productIdx] });
      productIdx += 1;
      index += 1;
      productsOnPage += 1;
    }

    // After rendering all products, check if there is a banner immediately after the last product of the page
    if (bannerIdx < sortedBanners.length && (sortedBanners[bannerIdx].position ?? 0) === index) {
      if (isLastPage) {
        // If this is the last page, render the last banner
        result.push({ type: 'banner', data: sortedBanners[bannerIdx] });
        bannerIdx += 1;
        index += 1;
      }
      // If not the last page, DO NOT render this banner, leave it for the next page
    }

    return result;
  };

  const mergeAccumulatedProductsWithBanners = (
    products: ProductListItem[],
    banners: ProductBanner[]
  ) => {
    const sortedBanners = [...banners].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const result: (
      | { type: 'product'; data: ProductListItem }
      | { type: 'banner'; data: ProductBanner }
    )[] = [];
    let productIdx = 0;
    let bannerIdx = 0;
    let index = 0;
    const totalProducts = products.length;

    while (productIdx < totalProducts || bannerIdx < sortedBanners.length) {
      if (bannerIdx < sortedBanners.length && (sortedBanners[bannerIdx].position ?? 0) === index) {
        result.push({ type: 'banner', data: sortedBanners[bannerIdx] });
        bannerIdx++;
        index++;
        continue;
      }
      if (productIdx < totalProducts) {
        result.push({ type: 'product', data: products[productIdx] });
        productIdx++;
        index++;
      } else {
        if (bannerIdx < sortedBanners.length) {
          result.push({ type: 'banner', data: sortedBanners[bannerIdx] });
          bannerIdx++;
          index++;
        }
      }
    }
    return result;
  };

  const getStoredBanners = (): ProductBanner[] => {
    try {
      const stored = getSessionStorage('banners');
      if (typeof stored === 'string') return JSON.parse(stored);
      return stored || [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (displayProducts.length === 0) {
      afterRender?.();
    }
  }, [displayProducts]);

  useEffect(() => {
    if (banners?.length) {
      setSessionStorage('banners', JSON.stringify(banners));
    }
  }, [banners]);

  const { productImg } = useProductItemThemeSettings();
  const currentPage = paginationState?.page || 1;
  const limitPerPage = paginationState?.limit || 24;
  const isAccumulated =
    (paginationType === 'load_more' && products.length > limitPerPage) ||
    (paginationType === 'infinite_scroll' &&
      (products.length > limitPerPage || (paginationState?.page ?? 1) > 1));

  // --- Improve: Save accumulated_page on every page change ---
  useEffect(() => {
    if (paginationType === 'infinite_scroll' && paginationState?.page) {
      setSessionStorage('accumulated_page', paginationState.page);
    }
  }, [paginationType, paginationState?.page]);

  const replayFetchedRef = useRef(false);
  const firstLoadRef = useRef<boolean>(true);

  useEffect(() => {
    if (
      paginationType === 'infinite_scroll' &&
      !replayFetchedRef.current &&
      products.length <= limitPerPage
    ) {
      const accumulatedPage = Number(getSessionStorage('accumulated_page'));
      if (accumulatedPage > 1) {
        replayFetchedRef.current = true;
        // Fetch only the accumulatedPage, not all previous pages
        changeProductPage(accumulatedPage, { loadProductsBehavior: 'replace' as any });
      }
    }
  }, [paginationType, products.length, limitPerPage, changeProductPage]);

  if (!loadingAdditional && firstLoadRef.current) {
    firstLoadRef.current = false;
  }

  if (firstLoadRef.current) {
    const productListType = viewAsGridMultiColListOption === 'list' ? 'list' : 'grid';
    const numberOfCols =
      viewAsGridMultiColListOption === 'list'
        ? 1
        : Number(viewAsGridMultiColListOption.split('--')[1]);

    return <ProductListPlaceholder columns={numberOfCols} productListType={productListType} />;
  }

  return (
    <ProductListPagination {...productListPaginationProps}>
      <div
        ref={productListElementRef}
        className={classnames(
          'product-list',
          className,
          getColClassName(),
          borderLayout === 'noBorder' && 'product-list--margin',
          borderLayout === 'hasPadding' && 'product-list--margin',
          borderLayout === 'noPadding' && 'product-list--border-no-margin',
          borderLayout === 'noPaddingImage' && 'product-list--border-no-margin'
        )}
      >
        <DeferredList
          delay={0}
          runOnceWhenItemsAvailable
          items={
            paginationType === 'default'
              ? mergeDisplayProductsWithBanners(
                  displayProducts,
                  getStoredBanners(),
                  currentPage,
                  limitPerPage,
                  totalProducts
                )
              : isAccumulated
              ? mergeAccumulatedProductsWithBanners(displayProducts, getStoredBanners())
              : mergeDisplayProductsWithBanners(
                  displayProducts,
                  getStoredBanners(),
                  currentPage,
                  limitPerPage,
                  totalProducts
                )
          }
          disableDeferredRender={disableDeferredRender}
          afterComplete={afterDeferredListRender}
          onItemRender={(item, index) => {
            if (item.type === 'banner') {
              return renderInlineBanner(item.data, productImg.cropPosition);
            }
            const product = item.data;
            return (
              <ProductItem
                autoAspectRatio={`1/${autoAspectRatio}`}
                key={`${product.id} ${product?.variant_id || ''}`}
                product={product}
                onClick={() => trackPosition(index, product.id)}
                onRender={
                  viewAsOptionProps.viewAsOption === 'list'
                    ? function (productItemProps) {
                        return (
                          <ProductItemListViewLayout
                            {...productItemProps}
                            noImageUrl={no_image_url}
                            hasCollection={hasCollection}
                          />
                        );
                      }
                    : undefined
                }
              />
            );
          }}
        />
      </div>
    </ProductListPagination>
  );
};

export default registryComponent('ProductList', ProductList);
